<template lang="pug">
InventoryTab(name="deal" :error="fetchError" @new="newDeal" :canView="userCanViewDeals" :canAddNew="userCanAddEditDeal")
  template(v-slot:filters)
    BriqInput(v-model="query" hideOptionalMarker placeholder="Search" data-test-id="search-deals-input")
    BriqCheckbox.show-inactive(label="Show inactive deals" v-model="showInactive" data-test-id="show-inactive-deals-checkbox")
  template(v-slot:overview)
    BriqDataTable(
      :data="tableData"
      :filter="filter"
      :allowDelete="false"
      :allowEdit="userCanAddEditDeal"
      :loading="isFetchingDeals"
      :initialSort="initialSort"
      @edit="editDeal"
      @click="clickDeal"
      data-test-id="deals-data-table"
      )
      template(v-slot:ProductCount="{ item }")
        div {{ displayDealPrice(item) }}
      template(v-slot:AvailableOnline="{ item }")
        div {{ item.AvailableOnline ? 'Yes' : 'No' }}
    div.mt-XS (*) Indicates that the price is a price per person. The price we show here is a from price. This price can change after input from the customer.
    DealSlideInPanel(
      :focusedItem="focusedItem"
      :sidePanelIsOpen="sidePanelIsOpen"
      :allowEdit="userCanAddEditDeal"
      @edit="editDeal"
      @close="sidePanelIsOpen = false"
      )
</template>

<script>
import BriqDataTable from '@v/components/BriqDataTable.vue'
import InventoryTab from '@v/inventory/InventoryTab.vue'
import DealSlideInPanel from './DealSlideInPanel.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import Permission from '@v/models/UserPermissionEnum.js'
import { always, toLower, includes, propSatisfies, pipe, propEq, both, ifElse, prop } from 'ramda'

export default {
  name: 'Deals',
  components: { BriqDataTable, InventoryTab, DealSlideInPanel },
  data() {
    return {
      query: null,
      showInactive: false,
      sidePanelIsOpen: false,
      focusedItem: null,
      initialSort: {
        sort: 'asc',
        sortField: 'InternalName',
      },
    }
  },
  computed: {
    ...mapState('inventory', ['deals', 'isFetchingDeals', 'fetchError']),
    ...mapGetters('briqState', ['userHasPermission']),
    tableData() {
      return {
        items: this.deals || [],
        headers: [
          { field: 'InternalName', label: 'Deal' },
          { field: 'ProductCount', label: 'Price per person' }, // todo: use deal_price_per_person on Price
          { field: 'AvailableOnline', label: 'Available online' }, // todo: format to Yes/No
        ],
      }
    },
    userCanAddEditDeal() {
      return this.userHasPermission(Permission.API_Admin_ProductGroup_Create)
    },
    userCanViewDeals() {
      return this.userHasPermission(Permission.API_Admin_ProductGroup_Index)
    },
    filter() {
      const byQuery = query => {
        if (!query) return always(true)
        const containsQuery = pipe(toLower, includes)(query)
        return propSatisfies(pipe(toLower, containsQuery), 'InternalName')
      }
      const byEnabled = !this.showInactive ? propEq('Enabled', true) : always(true)
      return both(byQuery(this.query), byEnabled)
    },
  },
  methods: {
    ...mapActions('inventory', ['maybeFetchDeals']),
    newDeal() {
      this.$router.push({ name: 'CreateDeal' })
    },
    editDeal({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/deals/${item.Key}/edit`)
      else this.$router.push({ name: 'EditDeal', params: { dealKey: item.Key } })
    },
    clickDeal({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/deals/${item.Key}/edit`)
      else {
        this.focusedItem = item
        this.sidePanelIsOpen = true
      }
    },

    displayDealPrice(item) {
      const price = pipe(
        ifElse(prop('DisplayIncludingTax'), prop('IncludingTax'), prop('ExcludingTax')),
        this.$formatCurrency,
      )(item.Price)
      const starSymbol = item.PriceIsStartingPrice ? '*' : ''
      return `${price}${starSymbol}`
    },
  },
  mounted() {
    this.maybeFetchDeals()
  },
}
</script>

<style scoped lang="stylus">
.show-inactive
  min-width 300px
</style>
